import { ReactElement, useEffect, useMemo, useState } from 'react';
import Header from 'containers/Header';
import {
  RfUiBreadcrumbs,
  RfUiButton,
  RfUiModal,
  RfUiMultiselect,
  RfUiDatepicker,
  RfUiSelect,
} from 'renfe-components';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  getAllUsuarios,
  insertarUsuarioPerfil,
  habilitarUsuarioPerfil,
  eliminarUsuarioPerfil,
} from 'store/usuarios/actions';
import { makeSelectAllUsuarios } from 'store/usuarios/selectors';

// Cursos
import { makeSelectCursosPracticaParse2 } from 'store/practicas/selectors';

// Alumnos
import { getAllAlumnos, insertarAlumnosCursos, eliminarAlumnosCursos } from 'store/cursos/actions';
import { makeSelectAllAlumnos } from 'store/cursos/selectors';

// Usuario logado
import { makeSelectUsername } from 'store/app/selectors';

// sociedades
import { getSociedadesRequest } from 'store/practicas/actions';
import { makeSelectSociedadesParse } from 'store/practicas/selectors';

import './styles.css';

/* ================= Utilidades ================= */
const getValuesFromUIJson = (uiJson: string): string[] => {
  try {
    return (JSON.parse(uiJson || '[]') as any[])
      .map((x) => x?.value)
      .filter(Boolean)
      .map((v: any) => String(v));
  } catch {
    return [];
  }
};

const toOptionsJson = (data: any): string => {
  if (!data) return '[]';

  try {
    if (typeof data === 'string') {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed) && parsed.length && parsed[0]?.label && parsed[0]?.value) {
        return data;
      }
      data = parsed;
    }
  } catch {
    return '[]';
  }

  if (!Array.isArray(data)) return '[]';

  const seenIds = new Set<string>();

  const normalized = data
    .map((g: any) => {
      const idAlumno = g?.ID_ALUMNO;
      const nombreAlumno = g?.NOMBRE_ALUMNO;

      if (!idAlumno || !nombreAlumno) return null;

      const idStr = String(idAlumno);
      if (seenIds.has(idStr)) return null;
      seenIds.add(idStr);

      return {
        label: `${idStr} - ${String(nombreAlumno)}`,
        value: idStr,
      };
    })
    .filter(Boolean);

  return JSON.stringify(normalized);
};

const normalizeSociedadesToOptions = (raw: any): Array<{ label: string; value: any }> => {
  try {
    if (typeof raw === 'string') raw = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  return raw
    .map((s: any) => {
      const id = s?.ID_SOCIEDAD ?? s?.id ?? s?.value?.ID_SOCIEDAD;
      const nombre = s?.NOMBRE_SOCIEDAD ?? s?.nombre ?? s?.value?.NOMBRE_SOCIEDAD;
      if (!id || !nombre) return null;

      const idStr = String(id);
      if (seen.has(idStr)) return null;
      seen.add(idStr);

      return {
        label: `${idStr} - ${String(nombre)}`,
        value: s,
      };
    })
    .filter(Boolean) as Array<{ label: string; value: any }>;
};


const parseArrayMaybeJson = (raw: any): any[] => {
  try {
    if (typeof raw === 'string') return JSON.parse(raw);
  } catch {
    return [];
  }
  return Array.isArray(raw) ? raw : [];
};

const extractTimestampMsString = (e: any): string => {
  if (e?.detail === undefined || e?.detail === null) return '';
  return String(e.detail);
};

// Selector single: devolver value (o '')
const getSingleValueFromUIJson = (uiJson: string): string => {
  const vals = getValuesFromUIJson(uiJson);
  return vals.length ? String(vals[0]) : '';
};

// Sociedad desde usuariosAll: primera fila del usuario con ID_PERFIL que empiece por "1"
const getSociedadAlumnoFromUsuariosAll = (usuariosAllRaw: any, idAlumno: string): string => {
  const arr = Array.isArray(usuariosAllRaw) ? usuariosAllRaw : [];
  const row = arr.find(
    (u: any) =>
      String(u?.ID_USUARIO) === String(idAlumno) && String(u?.ID_PERFIL ?? '').startsWith('1')
  );

  const sociedad = row?.SOCIEDAD_USUARIO;

  return sociedad ? String(sociedad) : '10000000';
};

// Mapeo perfil alumno (texto) -> idPerfil
const mapperfilAlumnoToPerfil = (situacion: string): string => {
  switch (situacion) {
    case 'Maquinista en prácticas':
      return '111';
    case 'Becario en prácticas':
      return '121';
    case 'Aspirante en prácticas':
      return '122';
    case 'Maquinista Ext. en prácticas':
      return '123';
    default:
      return '111';
  }
};

/* ================= Tipos locales ================= */
type FormState = {
  nombre: string;
  identificador: string;
  tipo: string;
  categoriaProfesional: string;
  perfilAlumno: string;
  sociedadId: string;
};

type ErrorsState = {
  nombre?: string;
  identificador?: string;
  alumnosSel?: string;
  cursosSel?: string;
  fechaFin?: string;
  tipo?: string;
  categoriaProfesional?: string;
  perfilAlumno?: string;
  sociedadId?: string;
};

type AltaNuevoValues = {
  idCursosArr: string[];
  idUsuario: string;
  nombre: string;
  tipo: string;
  categoriaProfesional: string;
  perfilAlumno: string;
};

const buildAltaNuevoValues = (form: FormState, selectedCursosUI: string): AltaNuevoValues => ({
  idCursosArr: getValuesFromUIJson(selectedCursosUI),
  idUsuario: String(form.identificador ?? '').trim(),
  nombre: String(form.nombre ?? '').trim(),
  tipo: String(form.tipo ?? '').trim(),
  categoriaProfesional: String(form.categoriaProfesional ?? '').trim(),
  perfilAlumno: String(form.perfilAlumno ?? '').trim(),
});

const validateAltaNuevo = (
  values: AltaNuevoValues,
  selectedFechaFinUI: string,
  sociedadId: string
): ErrorsState => {
  const errs: ErrorsState = {};

  if (!values.nombre) errs.nombre = 'El nombre es obligatorio';
  if (!values.idUsuario) errs.identificador = 'El identificador es obligatorio';
  if (!values.idCursosArr.length) errs.cursosSel = 'Seleccione al menos un curso';
  if (!selectedFechaFinUI) errs.fechaFin = 'Seleccione una fecha';
  if (!values.tipo) errs.tipo = 'El tipo es obligatorio';
  if (!values.categoriaProfesional) {
    errs.categoriaProfesional = 'La categoría profesional es obligatoria';
  }
  if (!values.perfilAlumno) errs.perfilAlumno = 'El perfil alumno es obligatorio';
  if (!sociedadId) errs.sociedadId = 'La sociedad es obligatoria';

  return errs;
};

const hasActiveAlumnoProfile = (usuariosAll: any, idUsuario: string): boolean =>
  (usuariosAll || []).some(
    (x: any) =>
      String(x?.ID_USUARIO) === idUsuario &&
      String(x?.ACTIVO).toUpperCase() === 'S' &&
      String(x?.ID_PERFIL).startsWith('1')
  );

const getPerfil111Status = (usuariosAll: any, idUsuario: string) => {
  const usuariosArr = Array.isArray(usuariosAll) ? usuariosAll : [];
  const isActivoS = (row: any) => String(row?.ID_ACTIVO ?? row?.ACTIVO ?? '').toUpperCase() === 'S';
  const isActivoN = (row: any) => String(row?.ID_ACTIVO ?? row?.ACTIVO ?? '').toUpperCase() === 'N';

  const userRowsPerfil111 = usuariosArr.filter(
    (u: any) => String(u?.ID_USUARIO) === idUsuario && String(u?.ID_PERFIL) === '111'
  );

  const perfil111Existe = userRowsPerfil111.length > 0;

  return {
    perfil111Existe,
    perfil111Activo: perfil111Existe && userRowsPerfil111.some(isActivoS),
    perfil111Inactivo: perfil111Existe && userRowsPerfil111.some(isActivoN),
  };
};

const getDuplicatedCursosAlumno = (
  alumnosData: any,
  idUsuario: string,
  idCursosArr: string[]
): string[] => {
  const aluData = parseArrayMaybeJson(alumnosData);
  const cursosSet = new Set(idCursosArr.map(String));

  return aluData
    .filter((r: any) => String(r?.ID_ALUMNO) === idUsuario)
    .map((r: any) => String(r?.ID_CURSO))
    .filter((c: string) => cursosSet.has(c));
};

const buildPayloadAlumnoCurso = (
  values: AltaNuevoValues,
  usuarioAltaBaja: any,
  selectedFechaFinUI: string,
  sociedadId: string
) => ({
  idCursos: values.idCursosArr.join(','),
  idAlumno: values.idUsuario,
  nombreAlumno: values.nombre,
  usuarioAlta: String(usuarioAltaBaja ?? ''),
  fechaIniAlumCurso: String(Date.now()),
  fechaFinAlumCurso: selectedFechaFinUI,
  usuarioModificacion: String(usuarioAltaBaja ?? ''),
  sociedadAlumno: String(sociedadId ?? '10000000'),
  sociedadCurso: '10000000',
  categoriaProfesional: values.categoriaProfesional,
});

const buildPayloadUsuario = (
  values: AltaNuevoValues,
  usuarioAltaBaja: any,
  sociedadId: string
) => ({
  idUsuario: values.idUsuario,
  nombreUsuario: values.nombre,
  idPerfil: mapperfilAlumnoToPerfil(values.perfilAlumno),
  usuarioAlta: String(usuarioAltaBaja ?? ''),
  sociedadUsuario: String(sociedadId ?? '10000000'),
  tipoUsuario: values.tipo,
});

/* ================= Selectores ================= */
const selectors = createStructuredSelector({
  cursos: makeSelectCursosPracticaParse2(),
  alumnosData: makeSelectAllAlumnos(),
  usuarioAltaBaja: makeSelectUsername(),
  usuariosAll: makeSelectAllUsuarios(),
  sociedades: makeSelectSociedadesParse(),
} as any);

export default function AltaAlumnos(): ReactElement {
  const dispatch = useDispatch();
  const { usuariosAll, cursos, alumnosData, usuarioAltaBaja, sociedades } = useSelector<any, any>(selectors);

  // Cargar alumnos y usuarios al montar
  useEffect(() => {
    dispatch(getAllAlumnos());
    dispatch(getAllUsuarios());
    dispatch(getSociedadesRequest() as any);
  }, [dispatch]);


  // Normalizamos opciones
  const alumnosOptions = useMemo(() => toOptionsJson(alumnosData), [alumnosData]);
  const sociedadesOptions = useMemo(() => JSON.stringify(normalizeSociedadesToOptions(sociedades)), [sociedades]);

  // Filtrar cursos que no estén CE/FI
  const cursosFiltrados = useMemo(() => {
    try {
      const list = typeof cursos === 'string' ? JSON.parse(cursos) : cursos;
      const filtered = (list || []).filter((c: any) => c?.state !== 'CE' && c?.state !== 'FI');
      return JSON.stringify(filtered);
    } catch {
      return '[]';
    }
  }, [cursos]);

  // Opciones selectores "Tipo" y "Categoría profesional"
  const tipoOptions = useMemo(
    () =>
      JSON.stringify([
        { label: 'INTERNO', value: 'INTERNO' },
        { label: 'EXTERNO', value: 'EXTERNO' },
      ]),
    []
  );

  const categoriaProfesionalOptions = useMemo(
    () =>
      JSON.stringify([
        { label: 'INTERNO', value: 'INTERNO' },
        { label: 'EXTERNO', value: 'EXTERNO' },
        { label: 'OPERADOR MANT.Y FABR. N2', value: 'OPERADOR MANT.Y FABR. N2' },
        { label: 'M.I. CONDUCCION/JEFE MAQ.', value: 'M.I. CONDUCCION/JEFE MAQ.' },
      ]),
    []
  );

  // Opciones selector "Perfil alumno"
  const situacionPractOptions = useMemo(
    () =>
      JSON.stringify([
        { label: 'Maquinista en prácticas', value: 'Maquinista en prácticas' },
        { label: 'Becario en prácticas', value: 'Becario en prácticas' },
        { label: 'Aspirante en prácticas', value: 'Aspirante en prácticas' },
        { label: 'Maquinista Ext. en prácticas', value: 'Maquinista Ext. en prácticas' },
      ]),
    []
  );

  // ======= Estado UI =======
  const [form, setForm] = useState<FormState>({
    nombre: '',
    identificador: '',
    tipo: '',
    categoriaProfesional: '',
    perfilAlumno: '',
    sociedadId: '',
  });
  const [selectedCursosUI, setSelectedCursosUI] = useState<string>('[]');
  const [selectedAlumnosUI, setSelectedAlumnosUI] = useState<string>('[]');

  const [cursosResetKey, setCursosResetKey] = useState(0);

  // Selectores "Tipo", "Categoría profesional" y "Perfil alumno" (single)
  const [selectedTipoUI, setSelectedTipoUI] = useState<string>('[]');
  const [selectedCategoriaProfUI, setSelectedCategoriaProfUI] = useState<string>('[]');
  const [selectedSituacionPractUI, setSelectedSituacionPractUI] = useState<string>('[]');

  const [tipoResetKey, setTipoResetKey] = useState(0);
  const [categoriaProfResetKey, setCategoriaProfResetKey] = useState(0);
  const [situacionPractResetKey, setSituacionPractResetKey] = useState(0);

  // Fecha fin obligatoria: timestamp ms como string
  const [selectedFechaFinUI, setSelectedFechaFinUI] = useState<string>(() => String(Date.now()));
  const [fechaResetKey, setFechaResetKey] = useState(0);
  const [selectedSociedadUI, setSelectedSociedadUI] = useState<string>('[]');
  const [sociedadResetKey, setSociedadResetKey] = useState(0);

  const [errors, setErrors] = useState<ErrorsState>({});

  const [saving, setSaving] = useState(false);

  // Modal reutilizable
  const [modalType, setModalType] = useState<'success' | 'alert' | 'confirm' | ''>('');
  const [modalMsg, setModalMsg] = useState('');
  const [onConfirmFn, setOnConfirmFn] = useState<(() => void) | null>(null);

  const abrir = (msg: string, tipo: 'success' | 'alert' | 'confirm' = 'success', fn?: () => void) => {
    setModalMsg(msg);
    setModalType(tipo);
    setOnConfirmFn(() => (fn ? fn : null));
  };
  const cerrar = () => {
    setModalType('');
    setModalMsg('');
    setOnConfirmFn(null);
  };

  const links = useMemo(
    () => [{ label: 'Alta alumnos', link: '/altaAlumnos', textHelp: 'Alta alumnos', active: true }],
    []
  );

  // margen según estado del menú lateral
  useEffect(() => {
    const btn = document.querySelector('div.ui-lateral-menu > button.ui-lateral-menu__menu-button');
    const sync = () => {
      if (!btn) return;
      const open = btn.classList.contains('ui-lateral-menu__menu-button--open');
      document.body.classList.toggle('left-menu-open', open);
    };
    let obs: MutationObserver | null = null;
    document.body.classList.add('left-menu-open');
    if (btn) {
      btn.addEventListener('click', sync);
      obs = new MutationObserver(sync);
      obs.observe(btn, { attributes: true, attributeFilter: ['class'] });
    }
    sync();
    return () => {
      if (btn) btn.removeEventListener('click', sync);
      if (obs) obs.disconnect();
      document.body.classList.remove('left-menu-open');
    };
  }, []);

  const TODAY_TS = useMemo(() => String(Date.now()), []);
  const MAX_TS = useMemo(() => String(new Date(2099, 11, 31).getTime()), []);

  const clearAlumnosSelector = () => {
    setSelectedAlumnosUI('[]');
  };

  const clearCursosSelector = () => {
    setSelectedCursosUI('[]');
    setCursosResetKey((k) => k + 1);
  };

  const clearFechaFin = () => {
    setSelectedFechaFinUI(TODAY_TS);
    setFechaResetKey((k) => k + 1);
  };

  const clearTipoYCategoriaYSituacion = () => {
    setSelectedTipoUI('[]');
    setSelectedCategoriaProfUI('[]');
    setSelectedSituacionPractUI('[]');
    setTipoResetKey((k) => k + 1);
    setCategoriaProfResetKey((k) => k + 1);
    setSituacionPractResetKey((k) => k + 1);
    setSelectedSociedadUI('[]');
    setSociedadResetKey((k) => k + 1);
  };

  const limpiar = () => {
    setForm({
      nombre: '',
      identificador: '',
      tipo: '',
      categoriaProfesional: '',
      perfilAlumno: '',
      sociedadId: '',
    });
    setErrors({});
    clearAlumnosSelector();
    clearCursosSelector();
    clearFechaFin();
    clearTipoYCategoriaYSituacion();
  };

  // ======= Handlers Selectores =======
  const handleSelectedCurso = (e: any) => {
    setSelectedCursosUI(JSON.stringify(e.detail));
    setErrors((p) => ({ ...p, cursosSel: undefined }));
  };

  const handleSelectedAlumno = (e: any) => {
    setSelectedAlumnosUI(JSON.stringify(e.detail));
    setErrors((p) => ({ ...p, alumnosSel: undefined }));
  };

  const handleFechaFinSelected = (e: any) => {
    const tsMs = extractTimestampMsString(e);
    setSelectedFechaFinUI(tsMs);
    setErrors((p) => ({ ...p, fechaFin: undefined }));
  };

  // Selector "Tipo" (single)
  const handleSelectedTipo = (e: any) => {
    const detail = Array.isArray(e?.detail) ? e.detail : [];
    const last = detail.length ? detail[detail.length - 1] : null;
    const singleArr = last ? [last] : [];
    setSelectedTipoUI(JSON.stringify(singleArr));

    const value = singleArr[0]?.value ?? '';
    setForm((p) => ({ ...p, tipo: String(value || '') }));
    setErrors((p) => ({ ...p, tipo: undefined }));
  };

  // Selector "Categoría profesional" (single)
  const handleSelectedCategoriaProf = (e: any) => {
    const detail = Array.isArray(e?.detail) ? e.detail : [];
    const last = detail.length ? detail[detail.length - 1] : null;
    const singleArr = last ? [last] : [];
    setSelectedCategoriaProfUI(JSON.stringify(singleArr));

    const value = singleArr[0]?.value ?? '';
    setForm((p) => ({ ...p, categoriaProfesional: String(value || '') }));
    setErrors((p) => ({ ...p, categoriaProfesional: undefined }));
  };

  // Selector "Perfil alumno" (single)
  const handleSelectedSituacionPract = (e: any) => {
    const detail = Array.isArray(e?.detail) ? e.detail : [];
    const last = detail.length ? detail[detail.length - 1] : null;
    const singleArr = last ? [last] : [];
    setSelectedSituacionPractUI(JSON.stringify(singleArr));

    const value = singleArr[0]?.value ?? '';
    setForm((p) => ({ ...p, perfilAlumno: String(value || '') }));
    setErrors((p) => ({ ...p, perfilAlumno: undefined }));
  };

  const handleSelectedSociedad = (e: any) => {
  const detail = Array.isArray(e?.detail) ? e.detail : [];
  const last = detail.length ? detail[detail.length - 1] : null;
  const singleArr = last ? [last] : [];
  setSelectedSociedadUI(JSON.stringify(singleArr));

  const v = singleArr[0]?.value;
  const idSoc = typeof v === 'string' ? v : String(v?.ID_SOCIEDAD ?? v?.value?.ID_SOCIEDAD ?? '');
  setForm((p) => ({ ...p, sociedadId: idSoc }));
  setErrors((p) => ({ ...p, sociedadId: undefined }));
};

  // ======= Alta desde selectores =======
  const onAltaFromSelectors = () => {
    const idAlumnos = getValuesFromUIJson(selectedAlumnosUI);
    const idCursos = getValuesFromUIJson(selectedCursosUI);

    const categoriaProfesionalSel = getSingleValueFromUIJson(selectedCategoriaProfUI);

    const errs: typeof errors = {};
    if (!idAlumnos.length) errs.alumnosSel = 'Seleccione al menos un alumno';
    if (!idCursos.length) errs.cursosSel = 'Seleccione al menos un curso';
    if (!selectedFechaFinUI) errs.fechaFin = 'Seleccione una fecha';
    if (!categoriaProfesionalSel) errs.categoriaProfesional = 'La categoría profesional es obligatoria';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const data = parseArrayMaybeJson(alumnosData);
    const cursosSet = new Set(idCursos.map((c) => String(c)));

    const duplicatesByAlumno: Record<string, string[]> = {};
    for (const idAlumno of idAlumnos) {
      const rows = data.filter((r: any) => String(r?.ID_ALUMNO) === String(idAlumno));
      const dupCursos = rows
        .map((r: any) => String(r?.ID_CURSO))
        .filter((idCurso: string) => cursosSet.has(idCurso));

      if (dupCursos.length) duplicatesByAlumno[String(idAlumno)] = Array.from(new Set(dupCursos));
    }

    const dupAlumnos = Object.keys(duplicatesByAlumno);
    if (dupAlumnos.length) {
      const lines = dupAlumnos.map(
        (a) => `Alumno ${a}: cursos ya asociados (${duplicatesByAlumno[a].join(', ')})`
      );
      abrir(`No se ha enviado ninguna petición.\n\nYa existen asociaciones:\n- ${lines.join('\n- ')}`, 'alert');
      return;
    }

    const nombreByAlumno = (idAlumno: string): string => {
      const row = data.find((r: any) => String(r?.ID_ALUMNO) === String(idAlumno));
      return String(row?.NOMBRE_ALUMNO ?? '');
    };

    const now = String(Date.now());
    const idCursosCsv = idCursos.join(',');
    const fechaFin = selectedFechaFinUI;

    setSaving(true);
    try {
      for (const idAlumno of idAlumnos) {
        const sociedadAlumno = getSociedadAlumnoFromUsuariosAll(usuariosAll, String(idAlumno));

        const payload = {
          idCursos: idCursosCsv,
          idAlumno: String(idAlumno),
          nombreAlumno: nombreByAlumno(String(idAlumno)),
          usuarioAlta: String(usuarioAltaBaja ?? ''),
          fechaIniAlumCurso: now,
          fechaFinAlumCurso: fechaFin,
          usuarioModificacion: String(usuarioAltaBaja ?? ''),
          sociedadAlumno,
          sociedadCurso: '10000000',
          categoriaProfesional: categoriaProfesionalSel,
        };

        dispatch(insertarAlumnosCursos(payload as any));
      }

      abrir(
        `Se han enviado las peticiones. (Alta: ${idAlumnos.length} alumno(s) · Cursos: ${idCursos.length})`,
        'success',
        () => {;
          clearAlumnosSelector();
        }
      );
      limpiar();
    } finally {
      setSaving(false);
    }
  };

  // ======= Baja desde selectores =======
  const onBajaFromSelectors = () => {
  const idAlumnosArr = getValuesFromUIJson(selectedAlumnosUI);
  const idCursosArr = getValuesFromUIJson(selectedCursosUI);

  const errs: typeof errors = {};
  if (!idAlumnosArr.length) errs.alumnosSel = 'Seleccione al menos un alumno';
  if (!idCursosArr.length) errs.cursosSel = 'Seleccione al menos un curso';
  setErrors(errs);
  if (Object.keys(errs).length) return;

  const data = parseArrayMaybeJson(alumnosData);

  // detectar cursos NO asociados por alumno
  const missingByAlumno: Record<string, string[]> = {};

  for (const idAlumno of idAlumnosArr) {
    const rowsAlumno = data.filter((r: any) => String(r?.ID_ALUMNO) === String(idAlumno));
    const cursosAsociados = new Set(rowsAlumno.map((r: any) => String(r?.ID_CURSO)).filter(Boolean));

    const faltan = idCursosArr
      .map(String)
      .filter((idCurso) => !cursosAsociados.has(String(idCurso)));

    if (faltan.length) missingByAlumno[String(idAlumno)] = Array.from(new Set(faltan));
  }

  const alumnosConFaltantes = Object.keys(missingByAlumno);

  // si falta alguna asociación no se hace ninguna baja
  if (alumnosConFaltantes.length) {
    const lines = alumnosConFaltantes.map(
      (a) => `Alumno ${a}: NO está asociado a cursos (${missingByAlumno[a].join(', ')})`
    );

    abrir(
      `No se ha enviado ninguna petición.\n\nFaltan asociaciones para los cursos seleccionados:\n- ${lines.join(
        '\n- '
      )}`,
      'alert'
    );
    return;
  }

  // si todo está asociado, entonces se hace la baja
  const payloadBaja = {
    idCursos: idCursosArr.join(','),
    idAlumnos: idAlumnosArr.join(','),
  };

  // Calcular alumnos que se quedarían sin cursos tras la baja
  const cursosBajaSet = new Set(idCursosArr.map(String));
  const alumnosSinCursosTrasBaja: string[] = [];

  for (const idAlumno of idAlumnosArr) {
    const rowsAlumno = data.filter((r: any) => String(r?.ID_ALUMNO) === String(idAlumno));
    const cursosActuales = Array.from(
      new Set(rowsAlumno.map((r: any) => String(r?.ID_CURSO)).filter(Boolean))
    );

    const cursosRestantes = cursosActuales.filter((c) => !cursosBajaSet.has(String(c)));

    if (cursosRestantes.length === 0) {
      alumnosSinCursosTrasBaja.push(String(idAlumno));
    }
  }

  dispatch(eliminarAlumnosCursos(payloadBaja as any));

  if (alumnosSinCursosTrasBaja.length) {
    dispatch(
      eliminarUsuarioPerfil({
        idUsuarios: alumnosSinCursosTrasBaja.join(','),
        idPerfil: '111',
        idUsuarioModificacion: String(usuarioAltaBaja ?? ''),
      } as any)
    );
  }

  abrir('Se ha enviado la petición de BAJA de los alumnos en los cursos seleccionados.', 'success', () => {
    clearAlumnosSelector();
  });

  limpiar();
};

  // ======= Alta Nuevo =======
  const onAltaNuevo = () => {
    const values = buildAltaNuevoValues(form, selectedCursosUI);
    const errs = validateAltaNuevo(values, selectedFechaFinUI, form.sociedadId);

    setErrors(errs);

    if (Object.keys(errs).length) {
      return;
    }

    // si existe ya el usuario
    if (hasActiveAlumnoProfile(usuariosAll, values.idUsuario)) {
      abrir('Este usuario ya existe.', 'alert');
      return;
    }

    const { perfil111Existe, perfil111Activo, perfil111Inactivo } = getPerfil111Status(
      usuariosAll,
      values.idUsuario
    );

    const dupCursos = getDuplicatedCursosAlumno(
      alumnosData,
      values.idUsuario,
      values.idCursosArr
    );

    if (dupCursos.length) {
      abrir(
        `No se ha enviado ninguna petición.\n\nEl usuario ${values.idUsuario} ya es alumno de los cursos: ${Array.from(
          new Set(dupCursos)
        ).join(', ')}`,
        'alert'
      );
      return;
    }

    const payloadAlumnoCurso = buildPayloadAlumnoCurso(
      values,
      usuarioAltaBaja,
      selectedFechaFinUI,
      form.sociedadId
    );

    setSaving(true);
    try {
      if (perfil111Activo) {
        dispatch(insertarAlumnosCursos(payloadAlumnoCurso as any));
        abrir('Se ha enviado la petición de ALTA del alumno y asociación al/los curso(s).', 'success', () => {
          setForm({
            nombre: '',
            identificador: '',
            tipo: '',
            categoriaProfesional: '',
            perfilAlumno: '',
            sociedadId: '',
          });
          dispatch(getAllAlumnos());
        });
        limpiar();
        return;
      }

      if (perfil111Inactivo) {
        dispatch(habilitarUsuarioPerfil({ idUsuarios: values.idUsuario, idPerfil: '111' } as any));
        dispatch(insertarAlumnosCursos(payloadAlumnoCurso as any));

        abrir(
          'Se ha habilitado el perfil 111 del usuario y se ha enviado la petición de ALTA del alumno y asociación al/los curso(s).',
          'success',
          () => {
            setForm({
              nombre: '',
              identificador: '',
              tipo: '',
              categoriaProfesional: '',
              perfilAlumno: '',
              sociedadId: '',
            });
            clearAlumnosSelector();
          }
        );

        limpiar();
        return;
      }

      if (!perfil111Existe) {
        const payloadUsuario = buildPayloadUsuario(values, usuarioAltaBaja, form.sociedadId);

        dispatch(insertarUsuarioPerfil(payloadUsuario as any));
        dispatch(insertarAlumnosCursos(payloadAlumnoCurso as any));

        abrir(
          'Se ha creado el perfil del usuario y se ha enviado la petición de ALTA del alumno y asociación al/los curso(s).',
          'success',
          () => {
            setForm({
              nombre: '',
              identificador: '',
              tipo: '',
              categoriaProfesional: '',
              perfilAlumno: '',
              sociedadId: '',
            });
            clearAlumnosSelector();
          }
        );
        limpiar();
        return;
      }

      abrir('No se ha podido determinar el estado del perfil 111 del usuario. Revise los datos.', 'alert');
    } finally {
      setSaving(false);
    }
  };



  return (
    <>
      <Header />
      <div className="alta-alumnos">
        <RfUiBreadcrumbs
          links={JSON.stringify(links)}
          onEventChange={(e: any) => {
            window.history.pushState({}, '', e.detail);
          }}
        />
        <h1 className="page-title">Alta alumnos</h1>

        {/* Selectores superiores: Alumno + Curso + Fecha fin */}
        <div className="rf-flex rf-flex-wrap rf-gap-16">
          <div className="form-group rf-flex-1">
            <RfUiMultiselect
              key={`alumnos-${alumnosOptions}`}
              options={alumnosOptions}
              defaultChecksRemake={selectedAlumnosUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione al menos una opción"
              text-label="Alumno"
              placeholder="Seleccione alumno(s)"
              onOptionSelected={handleSelectedAlumno}
            />
            {errors.alumnosSel && <div className="error">{errors.alumnosSel}</div>}
          </div>

          <div className="form-group rf-flex-1">
            <RfUiMultiselect
              key={`cursos-${cursosResetKey}`}
              options={cursosFiltrados}
              defaultChecksRemake={selectedCursosUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione al menos una opción"
              text-label="Curso"
              placeholder="Seleccione curso(s)"
              onOptionSelected={handleSelectedCurso}
            />
            {errors.cursosSel && <div className="error">{errors.cursosSel}</div>}
          </div>

          {/* Datepicker a la derecha del selector de cursos */}
          <div className="form-group rf-flex-1 fecha-fin-alumno">
            <RfUiDatepicker
              key={`fecha-fin-${fechaResetKey}`}
              position="left"
              moment-locale="es"
              labelInput="Fecha Fin"
              text-apply-button="Aplicar"
              defaultMinDateFrom={TODAY_TS}
              defaultMaxDateFrom={MAX_TS}
              defaultDateFrom={selectedFechaFinUI}
              onDateSelected={handleFechaFinSelected}
            />
            {errors.fechaFin && <div className="error">{errors.fechaFin}</div>}
          </div>
        </div>

        {/* Botones en línea: Alta + Baja (selectores) */}
        <div className="inline-actions">
          <RfUiButton disabled={saving} text="Alta" text-help="Alta" onButtonClick={onAltaFromSelectors} />
          <RfUiButton
            disabled={saving}
            aspect="secondary"
            className="btn-baja"
            text="Baja"
            text-help="Baja"
            onButtonClick={onBajaFromSelectors}
          />
        </div>

        {/* Formulario inferior (solo Alta Nuevo) */}
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            onAltaNuevo();
          }}
        >
          <div className="form-group">
            <div className="input-prefixed">
              <span className="prefix">Nombre</span>
              <input
                id="nombre"
                name="nombre"
                value={form.nombre}
                onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                placeholder="Introduzca nombre"
              />
            </div>
            <label htmlFor="nombre" className="field-label">
              Nombre del alumno
            </label>
            {errors.nombre && <div className="error">{errors.nombre}</div>}
          </div>

          <div className="form-group">
            <div className="input-prefixed">
              <span className="prefix">Identificador</span>
              <input
                id="identificador"
                name="identificador"
                value={form.identificador}
                onChange={(e) => setForm((p) => ({ ...p, identificador: e.target.value }))}
                placeholder="Introduzca identificador"
              />
            </div>
            <label htmlFor="identificador" className="field-label">
              Identificador del alumno
            </label>
            {errors.identificador && <div className="error">{errors.identificador}</div>}
          </div>

          {/* Selector TIPO (single) */}
          <div className="form-group single-multiselect">
            <RfUiMultiselect
              key={`tipo-${tipoResetKey}`}
              options={tipoOptions}
              defaultChecksRemake={selectedTipoUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione una opción"
              text-label="Tipo"
              placeholder="Seleccione tipo"
              onOptionSelected={handleSelectedTipo}
            />
            {errors.tipo && <div className="error">{errors.tipo}</div>}
          </div>

          {/* Selector CATEGORÍA PROFESIONAL (single) */}
          <div className="form-group single-multiselect categoria-prof">
            <RfUiMultiselect
              key={`categoria-prof-${categoriaProfResetKey}`}
              options={categoriaProfesionalOptions}
              defaultChecksRemake={selectedCategoriaProfUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione una opción"
              text-label="Categoría"
              placeholder="Seleccione categoría profesional"
              onOptionSelected={handleSelectedCategoriaProf}
            />
            {errors.categoriaProfesional && <div className="error">{errors.categoriaProfesional}</div>}
          </div>

          {/* Selector PERFIL ALUMNO (single) */}
          <div className="form-group single-multiselect">
            <RfUiMultiselect
              key={`situacion-pract-${situacionPractResetKey}`}
              options={situacionPractOptions}
              defaultChecksRemake={selectedSituacionPractUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione una opción"
              text-label="Perfil alumno"
              placeholder="Seleccione perfil alumno"
              onOptionSelected={handleSelectedSituacionPract}
            />
            {errors.perfilAlumno && <div className="error">{errors.perfilAlumno}</div>}
          </div>

          <div className="form-group single-multiselect">
            <RfUiMultiselect
              key={`sociedades-${sociedadResetKey}-${sociedadesOptions}`}
              options={sociedadesOptions}
              defaultChecksRemake={selectedSociedadUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione una opción"
              text-label="Sociedad"
              placeholder="Seleccione sociedad"
              onOptionSelected={handleSelectedSociedad}
            />
            {errors.sociedadId && <div className="error">{errors.sociedadId}</div>}
          </div>

          <div className="actions">
            <RfUiButton aspect="secondary" text="Limpiar filtro" text-help="Limpiar filtro" onButtonClick={limpiar} />
            <div className="actions-right">
              <RfUiButton
                disabled={saving}
                text={saving ? 'Guardando…' : 'Alta Nuevo'}
                text-help="Alta Nuevo"
                onButtonClick={onAltaNuevo}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Modal */}
      <RfUiModal
        title-modal={modalMsg}
        id-modal="modalAltaAlumno"
        modal-type={modalType}
        modalConfirmText="Aceptar"
        onModalClose={cerrar}
        onConfirm={() => {
          if (onConfirmFn) onConfirmFn();
          cerrar();
        }}
      />
    </>
  );
}
